export enum ProductTypes {
  NONE = "free",
  MONTHLY = "monthly",
  QUARTERLY = "quarterly",
  ANNUAL = "annual",
  TRAINING_ANNUAL = "training_annual",
  ANNUAL_DISCOUNT = "annual_discount",
  QUARTERLY_DISCOUNT = "quarterly_discount",
}

export const ProductNames: { [key in ProductTypes]: string } = {
  [ProductTypes.NONE]: "Free",
  [ProductTypes.MONTHLY]: "PokerGO Monthly Plan",
  [ProductTypes.QUARTERLY]: "PokerGO Quarterly Plan",
  [ProductTypes.ANNUAL]: "PokerGO Annual Plan",
  [ProductTypes.TRAINING_ANNUAL]: "PokerGO VIP Plan",
  [ProductTypes.ANNUAL_DISCOUNT]: "PokerGO Annual Plan",
  [ProductTypes.QUARTERLY_DISCOUNT]: "PokerGO Quarterly Plan",
};

export enum Store {
  NONE = "",
  STRIPE = "stripe",
  PAYPAL = "paypal",
}

export interface Product {
  name: string;
  price: number;
  striked?: string;
  promoCodePrice?: number;
  priceDescription: string;
  highlighted: boolean;
  isDiscountedPlan: boolean;
  productType: ProductTypes;
  planDetails: {
    title: string;
    details: {
      icon: string[];
      text: string;
    }[];
  }[];
  paypal_product_id?: string;
  stripe_product_id?: string;
  property_id?: string;
  symbol?: string;
  ribbon?: string;
}

export const baseProducts: {
  [key in Exclude<
    ProductTypes,
    | ProductTypes.NONE
    | ProductTypes.QUARTERLY_DISCOUNT
    | ProductTypes.ANNUAL_DISCOUNT
  >]: Product;
} = {
  [ProductTypes.TRAINING_ANNUAL]: {
    name: "PokerGO VIP",
    price: 299.99,
    priceDescription: "Billed Annually (equivalent to $24.99/month)",
    productType: ProductTypes.TRAINING_ANNUAL,
    highlighted: false,
    isDiscountedPlan: false,
    planDetails: [
      {
        title:
          "Premium poker coverage all year, exclusive VIP promotions, and PokerU",
        details: [
          {
            icon: ["fas", "check"],
            text: "100+ days of live poker each year",
          },
          {
            icon: ["fas", "check"],
            text: "24/7 on-demand access to all PokerGO content",
          },
          {
            icon: ["fas", "check"],
            text: "Stream WSOP, Super High Roller Bowl, Poker Masters & more",
          },
          {
            icon: ["fas", "check"],
            text: "Exclusive promotions",
          },
          {
            icon: ["fas", "check"],
            text: "Membership to PokerU: 100+ elite-level poker strategy videos powered by Run It Once",
          },
          {
            icon: ["fas", "check"],
            text: "$25 gift card to the PokerGO Shop",
          },
          {
            icon: ["fas", "check"],
            text: "Plan renews automatically, cancel renewal anytime",
          },
        ],
      },
    ],
  },
  [ProductTypes.ANNUAL]: {
    name: "Annual",
    price: 99.99,
    priceDescription: "Billed Annually (equivalent to $8.33/month)",
    productType: ProductTypes.ANNUAL,
    highlighted: false,
    isDiscountedPlan: false,
    planDetails: [
      {
        title: "Premium poker coverage all year plus exclusive promotions",
        details: [
          {
            icon: ["fas", "check"],
            text: "100+ days of live poker each year",
          },
          {
            icon: ["fas", "check"],
            text: "24/7 on-demand access to all PokerGO content",
          },
          {
            icon: ["fas", "check"],
            text: "Stream WSOP, Super High Roller Bowl, Poker Masters & more",
          },
          {
            icon: ["fas", "check"],
            text: "Exclusive promotions",
          },
          {
            icon: ["fas", "check"],
            text: "Save 58% vs. Monthly",
          },
          {
            icon: ["fas", "check"],
            text: "Plan renews automatically, cancel renewal anytime",
          },
          {
            icon: ["fas", "xmark"],
            text: "Does not include PokerU Training.",
          },
        ],
      },
    ],
  },
  [ProductTypes.QUARTERLY]: {
    name: "Quarterly",
    price: 39.99,
    priceDescription: "Billed Quarterly (equivalent to $13.33/month)",
    productType: ProductTypes.QUARTERLY,
    highlighted: false,
    isDiscountedPlan: false,
    planDetails: [
      {
        title: "Premium poker coverage every quarter",
        details: [
          {
            icon: ["fas", "check"],
            text: "100+ days of live poker per year",
          },
          {
            icon: ["fas", "check"],
            text: "24/7 on-demand access to PokerGO content",
          },
          {
            icon: ["fas", "check"],
            text: "Stream WSOP, Super High Roller Bowl, Poker Masters & more",
          },
          {
            icon: ["fas", "check"],
            text: "Save 33% vs. Monthly",
          },
          {
            icon: ["fas", "check"],
            text: "Plan renews automatically, cancel renewal anytime",
          },
          {
            icon: ["fas", "xmark"],
            text: "Does not include PokerU Training.",
          },
        ],
      },
    ],
  },
  [ProductTypes.MONTHLY]: {
    name: "Monthly",
    price: 19.99,
    priceDescription: "Billed Monthly",
    productType: ProductTypes.MONTHLY,
    highlighted: false,
    isDiscountedPlan: false,
    planDetails: [
      {
        title: "Premium poker coverage every month",
        details: [
          {
            icon: ["fas", "check"],
            text: "100+ days of live poker per year",
          },

          {
            icon: ["fas", "check"],
            text: "24/7 on-demand access to PokerGO content",
          },
          {
            icon: ["fas", "check"],
            text: "Stream WSOP, Super High Roller Bowl, Poker Masters & more",
          },
          {
            icon: ["fas", "check"],
            text: "Plan renews automatically, cancel renewal anytime",
          },
          {
            icon: ["fas", "xmark"],
            text: "Does not include PokerU Training",
          },
        ],
      },
    ],
  },
};

export const ProductConfig: {
  [key in Exclude<ProductTypes, ProductTypes.NONE>]: Product;
} = {
  ...baseProducts,
  [ProductTypes.ANNUAL_DISCOUNT]: {
    ...baseProducts[ProductTypes.ANNUAL],
    isDiscountedPlan: true,
    productType: ProductTypes.ANNUAL_DISCOUNT,
    ribbon: "SAVE $$$",
    striked: `$${baseProducts[ProductTypes.ANNUAL].price}`,
    price: 79.99,
    priceDescription:
      "Billed Annually (equivalent to $6.66 / month) <br/> AUTO RENEWS AT STANDARD RATE",
    planDetails: [
      {
        title:
          "Get 100+ days of live poker and 24/7 access to all PokerGO content!",
        details: [
          {
            icon: ["fas", "check"],
            text: "Stream WSOP, Super High Roller Bowl, Poker Masters, and more.",
          },
          {
            icon: ["fas", "check"],
            text: "Save 58% vs. monthly.",
          },
          {
            icon: ["fas", "check"],
            text: "Exclusive promotions for Annual and VIP members.",
          },
          {
            icon: ["fas", "check"],
            text: "Plan renews automatically at the standard price at the end of the promotional period, cancel anytime.",
          },
          {
            icon: ["fas", "xmark"],
            text: "Does not include PokerU Training.",
          },
        ],
      },
    ],
  },
  [ProductTypes.QUARTERLY_DISCOUNT]: {
    ...baseProducts[ProductTypes.QUARTERLY],
    isDiscountedPlan: true,
    productType: ProductTypes.QUARTERLY_DISCOUNT,
    ribbon: "SAVE $$$",
    striked: `$${baseProducts[ProductTypes.QUARTERLY].price}`,
    price: 29.99,
    priceDescription:
      "Billed Quarterly (equivalent to $9.99 / month) <br/> AUTO RENEWS AT STANDARD RATE",
    planDetails: [
      {
        title: "Enjoy premium poker coverage every quarter!",
        details: [
          {
            icon: ["fas", "check"],
            text: "Full livestream access to 100+ days of top poker events.",
          },
          {
            icon: ["fas", "check"],
            text: "24/7 access to on-demand classics.",
          },
          {
            icon: ["fas", "check"],
            text: "Save 33% vs. monthly.",
          },
          {
            icon: ["fas", "check"],
            text: "Plan renews automatically at the standard price at the end of the promotional period, cancel anytime.",
          },
          {
            icon: ["fas", "xmark"],
            text: "Does not include PokerU Training.",
          },
        ],
      },
    ],
  },
};

export const cancellationFlow = {
  [ProductTypes.MONTHLY]: {
    steps: [],
  },
  [ProductTypes.QUARTERLY]: {
    steps: [
      [
        ProductConfig[ProductTypes.MONTHLY],
        ProductConfig[ProductTypes.ANNUAL_DISCOUNT],
      ],
    ],
  },
  [ProductTypes.ANNUAL]: {
    steps: [
      [
        ProductConfig[ProductTypes.MONTHLY],
        ProductConfig[ProductTypes.QUARTERLY_DISCOUNT],
        ProductConfig[ProductTypes.ANNUAL_DISCOUNT],
      ],
    ],
  },
  [ProductTypes.TRAINING_ANNUAL]: {
    steps: [
      [
        ProductConfig[ProductTypes.MONTHLY],
        ProductConfig[ProductTypes.QUARTERLY_DISCOUNT],
        ProductConfig[ProductTypes.ANNUAL_DISCOUNT],
      ],
    ],
  },
};

export const upgradeFlow = {
  [ProductTypes.MONTHLY]: [
    ProductConfig[ProductTypes.QUARTERLY],
    ProductConfig[ProductTypes.ANNUAL],
    ProductConfig[ProductTypes.TRAINING_ANNUAL],
  ],
  [ProductTypes.QUARTERLY]: [
    ProductConfig[ProductTypes.ANNUAL],
    ProductConfig[ProductTypes.TRAINING_ANNUAL],
  ],
  [ProductTypes.ANNUAL]: [ProductConfig[ProductTypes.TRAINING_ANNUAL]],
  [ProductTypes.TRAINING_ANNUAL]: [],
};

export const RegisterSteps = [
  {
    route: "/register/select-plan",
  },
  {
    route: "/register",
  },
  {
    route: "/register/verify-email",
  },
  {
    route: "/register/checkout",
  },
];
